const FALLBACK_ICON = '/favicon.ico';
const RELATIVE_URL_PATTERN = /^(\/(?!\/)|\.{1,2}\/)/;

const ensureText = (value, fallback = '') => {
  if (typeof value !== 'string') {
    return fallback;
  }

  const trimmed = value.trim();
  return trimmed || fallback;
};

const getBaseOrigin = () => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return 'https://portfolio.local';
};

const sanitizeUrl = (value, { allowRelative = true, allowHttps = true } = {}) => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();

  if (!trimmed) {
    return null;
  }

  if (trimmed.startsWith('//')) {
    return null;
  }

  if (allowRelative && RELATIVE_URL_PATTERN.test(trimmed)) {
    return trimmed;
  }

  try {
    const parsed = new URL(trimmed, getBaseOrigin());

    if (allowHttps && parsed.protocol === 'https:') {
      return parsed.toString();
    }

    if (parsed.origin === getBaseOrigin() && (parsed.protocol === 'http:' || parsed.protocol === 'https:')) {
      return parsed.toString();
    }
  } catch (error) {
    return null;
  }

  return null;
};

const sanitizeMediaItem = (item, expectedType) => {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const url = sanitizeUrl(item.url);

  if (!url) {
    return null;
  }

  return {
    type: expectedType,
    url,
    caption: ensureText(item.caption, 'Untitled media'),
    description: ensureText(item.description),
  };
};

const sanitizeMediaGroup = (items, expectedType) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => sanitizeMediaItem(item, expectedType))
    .filter(Boolean);
};

const sanitizeLinks = (links) => {
  const normalizedLinks = Array.isArray(links)
    ? links
    : links && typeof links === 'object'
      ? [
          links.github && { url: links.github, label: 'GitHub Repository' },
          links.live && { url: links.live, label: 'Live Demo' },
        ].filter(Boolean)
      : [];

  return normalizedLinks
    .map((link) => {
      const url = sanitizeUrl(link?.url, { allowRelative: false, allowHttps: true });

      if (!url) {
        return null;
      }

      return {
        url,
        label: ensureText(link?.label, 'External Link'),
      };
    })
    .filter(Boolean);
};

const sanitizeImageGallery = (items) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => {
      const url = sanitizeUrl(item?.url);

      if (!url) {
        return null;
      }

      return {
        url,
        caption: ensureText(item.caption, 'Gallery image'),
        description: ensureText(item.description),
      };
    })
    .filter(Boolean);
};

export const sanitizeProjects = (projects) => {
  if (!Array.isArray(projects)) {
    return [];
  }

  return projects
    .map((project) => ({
      title: ensureText(project?.title, 'Untitled project'),
      icon: sanitizeUrl(project?.icon) || FALLBACK_ICON,
      description: ensureText(project?.description, 'Description unavailable.'),
      history: ensureText(project?.history),
      skills: Array.isArray(project?.skills)
        ? project.skills.map((skill) => ensureText(skill)).filter(Boolean)
        : [],
      workflow: Array.isArray(project?.workflow)
        ? project.workflow.map((step) => ensureText(step)).filter(Boolean)
        : ensureText(project?.workflow),
      media: {
        images: sanitizeMediaGroup(project?.media?.images, 'image'),
        videos: sanitizeMediaGroup(project?.media?.videos, 'video'),
        audio: sanitizeMediaGroup(project?.media?.audio, 'audio'),
      },
      images: sanitizeImageGallery(project?.images),
      links: sanitizeLinks(project?.links),
      lyrics: ensureText(project?.lyrics),
    }))
    .filter((project) => project.title && project.description);
};
